import { STATIC_PAGE_PATHS, DYNAMIC_PAGE_PATHS, DYNAMIC_PAGE_QUERY_KEYS } from "./constants/pagePaths";
import { getCurrentAppBase } from "./appBase";
import { getTemplate, isEcomTemplate } from "./templates";

export const composeLink = (link, currentLanguageCode = null, prependAppBase = true) => {
    let finalLink = link.startsWith("/") ? link : `/${link}`;

    const appBase = getCurrentAppBase();
    if (!finalLink.startsWith(appBase) && prependAppBase) finalLink = appBase + finalLink;

    if (currentLanguageCode && currentLanguageCode !== "en") return `/${currentLanguageCode}${finalLink}`;

    return finalLink;
};

export const getDynamicLink = (pageType, identifier) =>
    `${DYNAMIC_PAGE_PATHS[pageType]}?${DYNAMIC_PAGE_QUERY_KEYS[pageType] ? `${DYNAMIC_PAGE_QUERY_KEYS[pageType]}=` : ""}${identifier}`;

/**
 * Build a dynamic page link for a reference object that came back from the
 * Magnolia delivery endpoint with a multi-source resolver. JCR refs use the
 * `@path` of the node (unique across the tree); external refs use `@id` plus
 * an explicit `&source=` query so the page can route to the correct
 * /external/{source}/... endpoint instead of the JCR delivery. No `&source=`
 * is appended for JCR — that's the default in useDetailPageQuery, so URLs
 * stay clean.
 *
 * Also used by product list / cart components so links produced from an
 * external product object (which has `source`, `@id`, `@name`) carry the
 * `&source=` query forward into the detail page.
 */
export const buildDynamicLinkForRef = (pageType, ref) => {
    if (!ref) return getDynamicLink(pageType, "");

    // Defensive: if the resolver couldn't expand the value (e.g. the picked node
    // isn't in the configured workspace), Magnolia falls back to writing the raw
    // string. Try to parse it as the picker's JSON ref so we still get a usable
    // URL; otherwise treat it as a legacy bare identifier.
    if (typeof ref === "string") {
        const trimmed = ref.trim();
        if (trimmed.startsWith("{") || trimmed.startsWith("&#123;")) {
            try {
                const parsed = JSON.parse(trimmed.replace(/&quot;/g, '"').replace(/&#123;/g, "{").replace(/&#125;/g, "}"));
                return buildDynamicLinkForRef(pageType, parsed);
            } catch (_) { /* fall through */ }
        }
        return getDynamicLink(pageType, trimmed);
    }

    // Picker JSON ref (not yet resolved): {source, productId|categoryId, [id]}
    // For JCR, prefer @path — it's unique across the tree, whereas @name is
    // only unique among siblings. The picker stores @path in categoryId/productId
    // already; this fallback handles the case where ref is a resolved delivery
    // object that carries @path alongside @name/@id.
    const source = ref.source || "jcr";
    // JCR: @path first, then @id (a uuid — the services resolve either), and @name only as a
    // last resort: a bare name cannot be resolved to a node. This matters for refs resolved by
    // the multi-source resolver (e.g. navigation), whose FoundationCategory DTO carries @id and
    // @name but no @path — with @name preferred, category links became ?category=<name> and the
    // overview page found nothing.
    const identifier = source === "jcr"
        ? (ref["@path"] || ref["@id"] || ref.categoryId || ref.productId || ref.id || ref["@name"])
        : (ref["@id"] || ref["@name"] || ref.categoryId || ref.productId || ref.id);
    let url = getDynamicLink(pageType, identifier || "");
    if (source !== "jcr") {
        url += `&source=${encodeURIComponent(source)}`;
    }
    return url;
};

// HOME is the root of the CURRENT site (a shop's root page under /shops, or the legacy
// APP_BASE), so it cannot come from the build-time constant table.
export const getStaticPageLink = (pageType) => (pageType === "HOME" ? getCurrentAppBase() : STATIC_PAGE_PATHS[pageType]);

export const getRouteLink = (route) => composeLink(route);

export const constructArtificialLinkNode = ({ label, value }) => {
    return {
        label,
        target: {
            field: "artificial",
            artificial: value,
        },
    };
};

export const parseLinkNode = (linkNode, currentLanguageCode) => {
    const { label, target = {} } = linkNode;
    let { field: type } = target;
    const propValue = type && target[type] ? target[type] : null;
    let value;

    switch (type) {
        case "artificial":
            value = composeLink(propValue, currentLanguageCode);
            break;

        case "website":
            let template = getTemplate(propValue);

            if (isEcomTemplate(template)) {
                value = composeLink(propValue["@path"], currentLanguageCode);
            } else {
                value = propValue["@link"];
                type = "external";
            }
            break;

        case "ecommerce-categories":
            value = composeLink(buildDynamicLinkForRef("PRODUCT_OVERVIEW", propValue), currentLanguageCode);
            break;

        case "ecommerce-products":
            value = composeLink(buildDynamicLinkForRef("PRODUCT_DETAIL", propValue), currentLanguageCode);
            break;

        case "external":
            value = propValue;
            break;
    }

    return {
        value,
        label,
        type,
        hasLink: target.field && target.field !== "none" && value ? true : false,
    };
};
